import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Product } from '../../interface/product/product';
import { HttpService } from '../http/http.service';
import { InvoiceProduct } from '../../interface/product/invoice-product.interface';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private _productCache: InvoiceProduct[] | null = null;
  private _productCachePromise: Promise<InvoiceProduct[]> | null = null;

  constructor(private _httpProduct: HttpService) { }

  /**
   * Returns all products, using in-memory cache if available.
   * If a request is in progress, returns the same promise to avoid duplicate API calls.
   */
  public async getAll(): Promise<Product[]> {
 
    return firstValueFrom(this._httpProduct.get(`/Products`));
  }

  /**
   * Clears the product cache (call after add/update/delete if needed)
   */
  public clearProductCache() {
    this._productCache = null;
    this._productCachePromise = null;
  }
  public get(id: number): Promise<Product> {
    return firstValueFrom(this._httpProduct.get('/Products/' + id));
  }
  public add(data: Product): Promise<Product> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.post('/Products', data));
  }
  public update(id: number, data: Product): Promise<Product> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.put('/Products/', id, data));
  }
  public delete(id: number): Promise<Product> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.delete('/Products/', id));
  }
  public stock(id: number): Promise<any> {
    return firstValueFrom(this._httpProduct.get(`/Products/${id}/stock`));
  }

  public async invoiceProducts(force = false): Promise<InvoiceProduct[]> {
    if (!force && this._productCache) {
      return this._productCache;
    }
    if (!force && this._productCachePromise) {
      return this._productCachePromise;
    }
    this._productCachePromise = firstValueFrom(this._httpProduct.get('/Products/for-invoicing'));
    const respone = await this._productCachePromise;
    this._productCache = respone;
    this._productCachePromise = null;
    return respone;
  }

  public getVariant(productId: number, variantId:number): Promise<any> {
    return firstValueFrom(this._httpProduct.get('/Products/' + productId + '/variants/'+variantId));
  }
  public getVariants(productId: number): Promise<any> {
    return firstValueFrom(this._httpProduct.get('/Products/' + productId + '/variants'));
  }

  public postVariants(productId: number, variant: any): Promise<any> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.post('/Products/' + productId + '/variants', variant));
  }

  public patchVariants(productId: number, variantId: number, variant: any): Promise<any> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.patch('/Products/' + productId + '/variants/', variantId, variant));
  }
  public deleteVariants(productId: number, variantId: number): Promise<any> {
    this.clearProductCache();
    return firstValueFrom(this._httpProduct.delete('/Products/' + productId + '/variants/', variantId));
  }
}
